/**
 * Currency Input Component
 * Input field for currency values with formatting
 */

import * as React from 'react';
import { Input } from './input';
import { cn } from '@/lib/utils';

export interface CurrencyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value?: number | null;
  onChange?: (value: number | null) => void;
}

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ className, value, onChange, onBlur, ...props }, ref) => {
    const [displayValue, setDisplayValue] = React.useState<string>('');

    React.useEffect(() => {
      if (value !== null && value !== undefined) {
        setDisplayValue(value.toString());
      } else {
        setDisplayValue('');
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Allow empty string
      if (inputValue === '') {
        setDisplayValue('');
        onChange?.(null);
        return;
      }

      // Remove any non-numeric characters except decimal point
      const numericValue = inputValue.replace(/[^0-9.]/g, '');

      // Prevent multiple decimal points
      const parts = numericValue.split('.');
      const sanitized = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : numericValue;

      setDisplayValue(sanitized);

      // Convert to number
      const number = parseFloat(sanitized);
      if (!isNaN(number)) {
        onChange?.(number);
      } else {
        onChange?.(null);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Format to 2 decimal places on blur
      if (displayValue && !isNaN(parseFloat(displayValue))) {
        const number = parseFloat(displayValue);
        const formatted = number.toFixed(2);
        setDisplayValue(formatted);
        onChange?.(parseFloat(formatted));
      }
      onBlur?.(e);
    };

    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          $
        </span>
        <Input
          type="text"
          inputMode="decimal"
          className={cn('pl-7', className)}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          ref={ref}
          {...props}
        />
      </div>
    );
  }
);

CurrencyInput.displayName = 'CurrencyInput';

export { CurrencyInput };
